/**
 * Re-read on a timer while the page is visible (place manager, unit 1: the room
 * now, live). Hidden tab: the timer stops. Visible again: one read at once
 * (unless the last one is under `ms` old), then the timer restarts. No
 * websockets.
 *
 * The browser pieces (visibility, timers) come in through {@link PollEnv}, so
 * the start/stop rule is unit-testable without a DOM.
 */

/** How often the room reads refresh while visible. */
export const ROOM_POLL_MS = 15_000;

/** What the poll needs from the browser. */
export interface PollEnv {
  hidden(): boolean;
  /** Subscribe to visibility changes; returns the unsubscribe. */
  onVisibilityChange(fn: () => void): () => void;
  /** Call `fn` every `ms`; returns the cancel. */
  every(ms: number, fn: () => void): () => void;
  /** Call `fn` once after `ms`; returns the cancel. */
  after(ms: number, fn: () => void): () => void;
  now(): number;
}

/** The real browser: the page visibility API and `setInterval`. */
export function browserPollEnv(): PollEnv {
  return {
    hidden: () => document.visibilityState === "hidden",
    onVisibilityChange(fn) {
      document.addEventListener("visibilitychange", fn);
      return () => document.removeEventListener("visibilitychange", fn);
    },
    every(ms, fn) {
      const id = setInterval(fn, ms);
      return () => clearInterval(id);
    },
    after(ms, fn) {
      const id = setTimeout(fn, ms);
      return () => clearTimeout(id);
    },
    now: () => Date.now(),
  };
}

/** Call `tick` every `ms` while the page is visible. Returns the stop. */
export function startVisiblePoll(tick: () => void, ms: number, env: PollEnv = browserPollEnv()): () => void {
  let cancel: (() => void) | null = null;
  // Counted from the start: the caller does the first read itself.
  let last = env.now();
  const timed = () => {
    last = env.now();
    tick();
  };
  const run = () => {
    if (!cancel) cancel = env.every(ms, timed);
  };
  const pause = () => {
    cancel?.();
    cancel = null;
  };
  if (!env.hidden()) run();
  const unsubscribe = env.onVisibilityChange(() => {
    if (env.hidden()) return pause();
    if (cancel) return;
    // Back from hidden: read now if the numbers are stale, then resume.
    if (env.now() - last >= ms) timed();
    run();
  });
  return () => {
    pause();
    unsubscribe();
  };
}

/** A polled re-read that has not answered in this long is aborted and counts
 *  as failed, so one hung request cannot stall the poll behind it. The first
 *  read of a page is never timed: only the re-reads this poll makes. */
export const POLL_READ_TIMEOUT_MS = 10_000;

/** Re-read with {@link startVisiblePoll}, one read in flight at a time. Each
 *  read gets a signal that aborts after `timeoutMs`; a timed-out read reports
 *  `onFail` at once and frees the slot, so the next tick reads again. The
 *  caller does the first (untimed) read itself. Returns the stop, which also
 *  aborts a read in flight. */
export function startPolledRead<T>(opts: {
  read: (signal: AbortSignal) => Promise<T>;
  onData: (data: T) => void;
  onFail: (error: unknown) => void;
  ms?: number;
  timeoutMs?: number;
  env?: PollEnv;
}): () => void {
  const { read, onData, onFail, ms = ROOM_POLL_MS, timeoutMs = POLL_READ_TIMEOUT_MS, env = browserPollEnv() } = opts;
  let inFlight: AbortController | null = null;
  let stopped = false;
  const tick = () => {
    if (inFlight || stopped) return;
    const ctrl = new AbortController();
    inFlight = ctrl;
    let settled = false;
    const settle = () => {
      if (settled) return false;
      settled = true;
      cancelTimer();
      if (inFlight === ctrl) inFlight = null;
      return !stopped;
    };
    const cancelTimer = env.after(timeoutMs, () => {
      ctrl.abort(new Error("The read timed out."));
      if (settle()) onFail(new Error("The read timed out."));
    });
    read(ctrl.signal).then(
      (data) => settle() && onData(data),
      (e: unknown) => settle() && onFail(e),
    );
  };
  const stopPoll = startVisiblePoll(tick, ms, env);
  return () => {
    stopped = true;
    stopPoll();
    inFlight?.abort();
  };
}
